// src/pages/Employees/employeesThunks.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import {  
  saveEmployeeApi,
  deleteEmployeeApi,
  getDepartmentsApi,
  getDesignationApi,
  getEmployeeCodeApi,
  // other APIs that old UI actually uses:
  attendanceMonthlySummaryApi,
  getEmployeeMonthlyPayDetailsApi,
  getEmployeeUsersApi,
  getClientApi,
  getHrLocationsApi,
  getClientUserApi,
  getMapEmployeeUserApi,
  saveEmployeeUserApi,
  getEmailScheduleApi,
  saveEmailScheduleApi,
  getAllBranchUsersApi,
} from '../../apiservice/hrpayroll';
import { getEmployeesApi } from '../../apiservice/setting';

/**
 * Keep thunks limited to the APIs actually used in the old UI.
 * Add only APIs that are present in your old codebase (no extras).
 */

export const fetchEmployees = createAsyncThunk(
  'employees/fetch',
  async (params = null, { rejectWithValue }) => {
    try {
      const res = await getEmployeesApi(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchEmployeeCode = createAsyncThunk(
  'employees/fetchCode',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getEmployeeCodeApi();
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchDepartments = createAsyncThunk(
  'employees/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getDepartmentsApi();      
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchDesignations = createAsyncThunk(
  'employees/fetchDesignations',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getDesignationApi();      
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const saveEmployee = createAsyncThunk(
  "employees/save",
  async (payload, { rejectWithValue }) => {
    
    try {
      // ✅ If FormData is already built in UI, send it directly
      if (payload instanceof FormData) { 
        const res = await saveEmployeeApi(payload);
        return res.data;
      }

      // ✅ Otherwise, build it here (fallback case)
      const formData = new FormData();
      formData.append("jsonData", JSON.stringify(payload));

      const fileFields = [
        "employeePhoto",
        "eidScan",
        "passportScan",
        "visaScan",
        "labourContractIdScan",
        "labourContractScan",
        "clientLabourContractScan",
        "insuranceScan",
        "labourCancellationCopy",
        "visaCancellationCopy",
        "experienceLetter",
        "insuranceCancellationCopy",
        "insuranceCancelledRefund",
          "warningLetter",
  "memoDocument",
  "otherAgreementsDocument",
      ];

      fileFields.forEach((field) => {
        if (payload[field] && typeof payload[field] !== "string") {
          formData.append(field, payload[field]);
        }
      });

      const res = await saveEmployeeApi(formData);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);



export const saveEmployeeUser = createAsyncThunk(
  "employees/saveUser",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await saveEmployeeUserApi(formData);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const removeEmployee = createAsyncThunk(
  'employees/remove',
  async (employee_id, { rejectWithValue }) => {
    try {
      const res = await deleteEmployeeApi({ employee_id });
      return { id: employee_id, data: res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);



/* Optional supporting thunks used by Payslip/FinalSettlement components in old UI */
export const fetchAttendanceSummary = createAsyncThunk(
  'employees/fetchAttendanceSummary',
  async (payload, { rejectWithValue }) => {
    // payload: { emp_id, month, year }
    try {
      const res = await attendanceMonthlySummaryApi(payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchEmployeeMonthlyPayDetails = createAsyncThunk(
  'employees/fetchEmployeeMonthlyPayDetails',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await getEmployeeMonthlyPayDetailsApi(payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/* Small helpers if old UI uses these – remove if not needed */
export const fetchEmployeeUsers = createAsyncThunk(
  'employees/fetchEmployeeUsers',
  async ( _ , { rejectWithValue }) => {
    try {
      const res = await getEmployeeUsersApi(null);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchAllBranchUsers = createAsyncThunk(
  'employees/fetchAllBranchUsers',
  async ( _ , { rejectWithValue }) => {
    try {
      const res = await getAllBranchUsersApi(null);
      return res?.data?.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchMapEmployeeUsers = createAsyncThunk(
  'employees/fetchMapEmployeeUsers',
  async ( _ , { rejectWithValue }) => {
    try {
      const res = await getMapEmployeeUserApi(null);
      const data = res?.data?.data;
      const value = data.map((item) => ({
          value: item.id,
          display: item.user_name,
          label: item.user_name,
          email: item.email,
      }));
      return value;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchClients = createAsyncThunk(
  'employees/fetchClients',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getClientApi();
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchClientUsers = createAsyncThunk(
  'employees/fetchClientUsers',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getClientUserApi(null);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchEmailSchedules = createAsyncThunk(
  'employees/fetchEmailSchedules',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getEmailScheduleApi(null);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


export const fetchHrLocations = createAsyncThunk(
  'employees/fetchHrLocations',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await getHrLocationsApi(payload || null);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
